import "polyfill-object.fromentries";
import apiFetch from "@wordpress/api-fetch";
import Cookies from "js-cookie";

import { formatPercent, getErrorMessage } from "./utils";
import type {
  ExistingForms,
  GetPollResponse,
  PollUpdateType,
  SubmitPollResponse,
  Votes,
} from "./types";

const { restNonce, messages } = newsner_polls_data;
const TRANSITION_DURATION = 100;

const rootUrlMiddleware = apiFetch.createRootURLMiddleware("/wp-json/");
const nonceMiddleware = apiFetch.createNonceMiddleware(restNonce);
apiFetch.use(rootUrlMiddleware);
apiFetch.use(nonceMiddleware);

/**
 * Initializes a form inside a container and adds a submit event listener to it.
 *
 * @param container - The container element that contains the form.
 * @param existingForms - A Set of existing forms to prevent adding duplicate event listeners.
 */
async function initForm(
  container: HTMLDivElement,
  existingForms: ExistingForms
) {
  const form = container.querySelector("form");

  if (form instanceof HTMLFormElement && !existingForms.has(form)) {
    form.addEventListener("submit", (e) => handleSubmit(e, container, form));

    existingForms.add(form);
  }

  displayCookieMessage(container);
  const canVote = await fetchAndUpdatePollVotes(container);

  if (!canVote) {
    form?.remove();
    showResults(container);
  }

  gtag("event", "newsner_poll_viewed", {
    event_category: "poll",
    event_label: container.id,
  });

  container.classList.remove("newsner-polls-pre-init");
}

function fetchAndUpdatePollVotes(container: HTMLDivElement) {
  return new Promise<boolean>(async (resolve) => {
    // prettier-ignore
    const pollIdBase64 = container.dataset?.poll;

    if (!pollIdBase64) {
      return;
    }

    try {
      const res = await apiFetch<GetPollResponse>({
        path: `newsner-polls/v1/poll/${pollIdBase64}?block=${container.id}`,
      });

      const results = container.querySelector<HTMLDivElement>(".results");
      const choices = container.querySelectorAll<HTMLDivElement>(".choice");

      setTotalVotes(res.data.total, results, "no change");
      setPercentData(choices, res.data.votes);
      updateAllPercentBarWidths(res.data.total, container, "no change");

      resolve(res.data.can_vote);
    } catch (e) {
      console.error(getErrorMessage(e));

      displayAlert(container, "error", messages.general_error);
    }

    resolve(false);
  });
}

/**
 * Handles the form submission event.
 *
 * @param e - The submit event.
 * @param container - The HTML div element that contains the form.
 * @param form - The HTML form element.
 * @returns A Promise that resolves when the form submission is complete.
 */
async function handleSubmit(
  e: SubmitEvent,
  container: HTMLDivElement,
  form: HTMLFormElement
) {
  e.preventDefault();
  removeAlerts(container);

  const formData = new FormData(form);

  if (e?.submitter instanceof HTMLButtonElement) {
    formData.append("choice", e.submitter.value);
  }
  formData.append("poll", container.dataset.poll ?? "");

  const data = Object.fromEntries(formData.entries());
  const choice = String(data.choice);
  const resetCount = optimisticallyUpdateCount(choice, container);
  const removeSelectedChoice = updateSelectedChoice(choice, container);

  try {
    const { success, message } = await apiFetch<SubmitPollResponse>({
      path: "newsner-polls/v1/poll",
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (success) {
      console.info(message);

      gtag("event", "newsner_poll_submitted", {
        event_category: "poll",
        event_label: container.id,
        value: choice,
      });
    } else {
      throw new Error("Problem sending form");
    }
  } catch (e) {
    const message = getErrorMessage(e);

    console.error(message);

    displayAlert(container, "error", messages.general_error);
    resetCount();
    removeSelectedChoice();
  } finally {
    showResults(container);
    removeForm(form);
  }
}

/**
 * Initializes all poll forms in the document with the class "newsner-polls-block-container".
 *
 * @returns A function that can be called to re-initialize all poll forms.
 */
function setupForms() {
  const existingForms = new Set<HTMLFormElement>([]);

  return () => {
    document
      .querySelectorAll<HTMLDivElement>(".newsner-polls-block-container")
      .forEach((container) => initForm(container, existingForms));
  };
}

/**
 * Handles displaying an alert message in a container element.
 *
 * @param container - The container element to display the alert in.
 * @param type - The type of alert to display. Defaults to "success".
 * @param message - The message to display in the alert. Defaults to an empty string.
 */
function displayAlert(
  container: HTMLDivElement,
  type = "success",
  message = ""
) {
  const alert = container.querySelector(`.${type}`);
  const messageEl = alert?.querySelector(".message");

  if (messageEl instanceof HTMLDivElement) {
    messageEl.textContent = message;
  }

  if (alert instanceof HTMLDivElement) {
    alert.classList.remove("np-hidden");
  }
}

function removeAlerts(container: HTMLDivElement) {
  container
    .querySelectorAll(".np-alert")
    .forEach((alert) => alert.classList.add("np-hidden"));
}

function removeForm(form: HTMLFormElement) {
  setTimeout(() => {
    form.remove();
  }, TRANSITION_DURATION);
}

/**
 * Shows the poll results by removing the "np-hidden" class from the results container
 * and adding the "np-animate-grow-x" class to each percent bar.
 *
 * @param container - The HTMLDivElement that contains the poll results.
 */
function showResults(container: HTMLDivElement) {
  const results = container.querySelector(".results");
  const percentBars = container.querySelectorAll(".percent-bar");

  setTimeout(() => {
    results?.classList.remove("np-hidden");

    percentBars.forEach((percentBar) => {
      percentBar.classList.add("np-animate-grow-x");
    });
  }, TRANSITION_DURATION);
}

/**
 * Updates the count of a choice in the UI optimistically.
 *
 * @param choice - The choice to update the count for.
 * @param container - The container element that holds the poll choices and results.
 * @returns A function that can be called to undo the optimistic update.
 */
function optimisticallyUpdateCount(choice: string, container: HTMLDivElement) {
  // if someone for some reason adds two or more choices with the same value
  // we need to update all of them in the UI
  const choiceElements = container.querySelectorAll<HTMLDivElement>(
    `[data-choice="${choice}"]`
  );

  const results = container.querySelector<HTMLDivElement>(".results");
  const totalVotes = Number(results?.dataset.votes);

  updatePercentData(choiceElements, "add");
  setTotalVotes(totalVotes, results, "add");
  updateAllPercentBarWidths(totalVotes, container, "add");

  return () => {
    updatePercentData(choiceElements, "subtract");
    setTotalVotes(totalVotes, results, "subtract");
    updateAllPercentBarWidths(totalVotes, container, "subtract");
  };
}

/**
 * Updates the percentage data for the given choice elements.
 *
 * @param choiceElements - The choice elements to update.
 * @param type - The type of update to perform. Defaults to "add".
 */
function updatePercentData(
  choiceElements: NodeListOf<HTMLDivElement>,
  type: PollUpdateType = "add"
) {
  choiceElements.forEach((choiceElement) => {
    let currentCount = 0;
    const count = Number(choiceElement?.dataset?.count);

    if (type === "add") {
      currentCount = count + 1;
    } else if (type === "subtract") {
      currentCount = count - 1;
    } else {
      currentCount = count;
    }

    if (choiceElement?.dataset?.count) {
      choiceElement.dataset.count = String(currentCount);
    }
  });
}
/**
 * Updates the percentage data for the given choice elements.
 *
 * @param choiceElements - The choice elements to update.
 * @param type - The type of update to perform. Defaults to "add".
 */
function setPercentData(
  choiceElements: NodeListOf<HTMLDivElement>,
  votes: Votes
) {
  choiceElements.forEach((choiceElement) => {
    let currentCount = 0;

    currentCount = votes[choiceElement.dataset.choice ?? ""] ?? 0;

    if (choiceElement?.dataset?.count) {
      choiceElement.dataset.count = String(currentCount);
    }
  });
}

function setTotalVotes(
  totalVotes: number,
  results: HTMLDivElement | null,
  type: PollUpdateType = "add"
) {
  const newTotalVotes = handleTotalVotesChange(type, totalVotes);
  if (results?.dataset?.votes) {
    results.dataset.votes = String(newTotalVotes);
  }
}

/**
 * Updates the width of all percent bars and count text elements in a poll container
 * based on the total number of votes and the type of update.
 *
 * @param totalVotes - The total number of votes in the poll.
 * @param container - The HTMLDivElement that contains the poll choices.
 * @param type - The type of update to perform. Defaults to "add".
 */
function updateAllPercentBarWidths(
  totalVotes: number,
  container: HTMLDivElement,
  type: PollUpdateType = "add"
) {
  const newTotalVotes = handleTotalVotesChange(type, totalVotes);
  const choicesElements = container.querySelectorAll<HTMLDivElement>(".choice");

  choicesElements.forEach((choice) => {
    const count = Number(choice?.dataset?.count);
    const countEl = choice.querySelector<HTMLDivElement>(".count");
    const percentBar = choice.querySelector<HTMLDivElement>(".percent-bar");

    if (
      countEl instanceof HTMLDivElement &&
      percentBar instanceof HTMLDivElement
    ) {
      const choicePercent = (count / newTotalVotes) * 100;
      const choicePercentRounded = Math.round(choicePercent * 10) / 10;

      percentBar.style.width = `${choicePercentRounded}%`;
      countEl.textContent = formatPercent(choicePercentRounded);
    }
  });
}

function handleTotalVotesChange(
  type: PollUpdateType = "add",
  totalVotes: number
) {
  switch (type) {
    case "add":
      return totalVotes + 1;
    case "subtract":
      return totalVotes - 1;
    default:
      return totalVotes;
  }
}

/**
 * Updates the selected choice in the poll container by adding or removing the "np-font-bold"
 * class to the choice text element.
 *
 * @param choice - The choice to select.
 * @param container - The poll container element.
 * @returns A function that removes the "np-font-bold" class from the selected choice text element.
 */
function updateSelectedChoice(choice: string, container: HTMLDivElement) {
  const prevChoiceElements =
    container.querySelectorAll<HTMLDivElement>(".choice");

  prevChoiceElements.forEach((prevChoiceElement) => {
    const text = prevChoiceElement.querySelector<HTMLDivElement>(".text");

    if (text instanceof HTMLDivElement) {
      text.classList.remove("np-font-bold");
    }
  });

  const choiceElements = container.querySelectorAll<HTMLDivElement>(
    `[data-choice="${choice}"]`
  );

  choiceElements.forEach((choiceElement) => {
    const text = choiceElement.querySelector<HTMLDivElement>(".text");

    if (text instanceof HTMLDivElement) {
      text.classList.add("np-font-bold");
    }
  });

  return () => {
    choiceElements.forEach((choiceElement) => {
      const text = choiceElement.querySelector<HTMLDivElement>(".text");

      if (text instanceof HTMLDivElement) {
        text.classList.remove("np-font-bold");
      }
    });
  };
}

function displayCookieMessage(container: HTMLDivElement) {
  if (Cookies.get("_ga") === undefined) {
    container.querySelector("form")?.remove();

    displayAlert(container, "error", messages.allow_cookies);
    showResults(container);
  }
}

window.addEventListener("DOMContentLoaded", () => {
  const initForms = setupForms();

  initForms();

  // Listen for event for when content is added to .content_wrapper
  // This is needed to support infinite scroll
  document.addEventListener("content-added", initForms);
});
